import { type ApolloError, gql, useQuery } from '@apollo/client';

import { deriveOnboardingScreen } from '../../lib/onboarding/deriveOnboardingScreen';
import { type OnboardingScreen } from '../../lib/onboarding/screens';

import type { GetOnboardingStateQuery, GetOnboardingStateQueryVariables } from './__generated__/useOnboardingState.gql';

/**
 * Shared family-shape fragment used by both onboarding queries
 * (`GetOnboardingState`, `GetDependants`) and by mutations whose
 * response returns the parent family (`CreateDependantAndRelationship`,
 * `LinkDependants`). The fragment is the superset of what either query
 * selects on Family — mutations carry it back in their response so Apollo's
 * normalized cache picks up updates to `Family.caregiverRelationships` and
 * `Family.dependantPartnerships` without a separate refetch round-trip.
 */
export const ONBOARDING_FAMILY_FRAGMENT = gql`
  fragment OnboardingFamilyFragment on Family {
    id
    # We derive the user's dependants from caregiverRelationships rather
    # than family.dependants directly — see IOnboardingProxyData JSDoc in
    # deriveOnboardingScreen.ts.
    caregiverRelationships {
      id
      relationship
      isStep
      isInLaw
      livesWithCaregiver
      caregiver {
        id
      }
      dependant {
        id
        firstName
        lastName
        preferredName
        dateOfBirth
        email
        phoneNumber
      }
    }
    dependantPartnerships {
      id
      liveTogether
      dependants {
        id
      }
    }
  }
`;

const GET_ONBOARDING_STATE = gql`
  query GetOnboardingState {
    getCurrentUser {
      id
      phoneNumber
      phoneNumberVerifiedAt
      primaryResidence {
        id
        addressLine1
        addressLine2
        city
        state
        postalCode
        country
        placeId
        formatted
      }
      financialAccounts {
        id
      }
    }
    getCurrentFamily {
      ...OnboardingFamilyFragment
      vehicles {
        id
      }
    }
  }
  ${ONBOARDING_FAMILY_FRAGMENT}
`;

export interface IUseOnboardingStateResult {
  /**
   * Screen the wizard should mount, or `null` when the user is fully
   * onboarded. While the query is in flight with no cached data,
   * `resumeScreen` is `null` — pair with `loading` at the gate so the
   * cold-load window isn't mistaken for "fully onboarded."
   */
  resumeScreen: OnboardingScreen | null;
  /**
   * `true` once we've derived a non-null screen. Stays `false` while we
   * have no data to derive from (cold load) so a caller reading this
   * without also checking `loading` doesn't briefly mistake the loading
   * window for "fully onboarded." Pair with `loading`: render nothing
   * (or a spinner) while `loading`, then branch on `needsOnboarding`.
   */
  needsOnboarding: boolean;
  /**
   * `true` when the user's phone number has been verified. Used by the
   * wizard to skip `PHONE_NUMBER_VERIFY` during back/next navigation —
   * symmetric with the resume derivation, which already treats verify
   * as a forward-only screen.
   */
  isPhoneVerified: boolean;
  /**
   * The user's primary residence row when one is saved, or `null`. Exposed
   * raw so step components (e.g. `UserResidenceStep`) can route re-saves
   * through the upsert update path with the existing `residenceId` and
   * seed the form with the persisted address fields.
   */
  primaryResidence: {
    id: string;
    addressLine1: string | null;
    addressLine2: string | null;
    city: string;
    state: string | null;
    postalCode: string | null;
    country: string;
    placeId: string | null;
    formatted: string | null;
  } | null;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<unknown>;
}

/**
 * Derives the user's onboarding entry screen from proxy-object presence
 * (dependants, caregiver / partnership links, financial accounts, phone
 * number entry). Replaces the previous `OnboardingProgressController`-backed
 * gate — see `lib/onboarding/screens.ts` for the rationale.
 *
 * Composes its own query rather than reading from `useCurrentUser` so the
 * proxy-object payload (family + dependants + linkages + financial-account
 * stubs) doesn't ride along on every page. Identity fields it shares with
 * other callers de-dup through the normalized `User` / `Family` records in
 * Apollo's cache rather than double-fetching.
 */
export const useOnboardingState = (): IUseOnboardingStateResult => {
  const { data, loading, error, refetch } = useQuery<GetOnboardingStateQuery, GetOnboardingStateQueryVariables>(
    GET_ONBOARDING_STATE,
    {
      fetchPolicy: 'cache-and-network',
    }
  );

  const user = data?.getCurrentUser ?? null;
  const family = data?.getCurrentFamily ?? null;

  if (!user || !family) {
    return {
      resumeScreen: null,
      needsOnboarding: false,
      isPhoneVerified: false,
      primaryResidence: null,
      loading,
      error,
      refetch,
    };
  }

  // Filter caregiverRelationships to the current user, then project the
  // dependant fields the derivation needs. Skips rows owned by a co-caregiver
  // in the same family — those aren't this user's responsibility to confirm.
  const myRelationships = family.caregiverRelationships.filter((r) => r.caregiver.id === user.id);
  const dependants = myRelationships.map((r) => ({ id: r.dependant.id }));

  // `livesWithCaregiver` / `liveTogether` non-null = the residence step's
  // "live with you?" question was answered. See `isFormReady` coupling noted
  // on IOnboardingProxyData.hasDependantResidenceInfo.
  const hasDependantResidenceInfo =
    myRelationships.some((r) => r.livesWithCaregiver !== null && r.livesWithCaregiver !== undefined) ||
    family.dependantPartnerships.some((p) => p.liveTogether !== null && p.liveTogether !== undefined);

  const resumeScreen = deriveOnboardingScreen({
    user: {
      id: user.id,
      phoneNumber: user.phoneNumber ?? null,
    },
    hasPrimaryResidence: user.primaryResidence !== null && user.primaryResidence !== undefined,
    dependants,
    dependantPartnerships: family.dependantPartnerships.map((p) => ({
      dependants: p.dependants.map((d) => ({ id: d.id })),
    })),
    hasDependantResidenceInfo,
    vehicleCount: family.vehicles.length,
    financialAccountCount: user.financialAccounts.length,
  });

  return {
    resumeScreen,
    needsOnboarding: resumeScreen !== null,
    isPhoneVerified: user.phoneNumberVerifiedAt !== null && user.phoneNumberVerifiedAt !== undefined,
    // Normalize codegen's `undefined` to `null` so the return shape carries
    // tight `string | null` on every nullable column.
    primaryResidence: user.primaryResidence
      ? {
          id: user.primaryResidence.id,
          addressLine1: user.primaryResidence.addressLine1 ?? null,
          addressLine2: user.primaryResidence.addressLine2 ?? null,
          city: user.primaryResidence.city,
          state: user.primaryResidence.state ?? null,
          postalCode: user.primaryResidence.postalCode ?? null,
          country: user.primaryResidence.country,
          placeId: user.primaryResidence.placeId ?? null,
          formatted: user.primaryResidence.formatted ?? null,
        }
      : null,
    loading,
    error,
    refetch,
  };
};
